import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { 
    ArrowLeft, Loader2, Check, Sparkles, Filter, Plus, Trash2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASIFICACIONES = [
    { id: 'avance_real', label: 'Avance real', color: 'text-[#5FAF9D]', bg: 'bg-[#5FAF9D]' },
    { id: 'pseudoavance', label: 'Pseudoavance', color: 'text-[#E8B27A]', bg: 'bg-[#E8B27A]' },
    { id: 'mantenimiento', label: 'Mantenimiento', color: 'text-[#7A9BA8]', bg: 'bg-[#7A9BA8]' },
    { id: 'evitacion_sofisticada', label: 'Evitación sofisticada', color: 'text-[#D07F7E]', bg: 'bg-[#D07F7E]' }
];

const emptyEntry = () => ({ accion: '', sensacion_productividad: 5, impacto_real: 3, clasificacion: '' });

export default function Modulo33FiltroImpacto() {
    const [entradas, setEntradas] = useState(Array.from({ length: 6 }, emptyEntry));
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/filtro_impacto`, { withCredentials: true });
                if (data.data?.entradas?.length) setEntradas(data.data.entradas);
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch { /* no draft */ } finally { setLoadingDraft(false); }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'filtro_impacto', data: { entradas } }, { withCredentials: true });
        } catch { /* silent */ } finally { setSavingDraft(false); }
    }, [entradas]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [entradas, saveDraft, loadingDraft, readOnly]);

    const updateEntry = (idx, field, value) => {
        setEntradas(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
    };

    const val = (v) => typeof v === 'object' ? v[0] : v;

    const filledEntries = entradas.filter(e => e.accion.trim().length > 0 && e.clasificacion);
    const canSubmit = !readOnly && (filledEntries.length >= 6);

    const pctPseudo = filledEntries.length > 0 ? Math.round((filledEntries.filter(e => e.clasificacion === 'pseudoavance').length / filledEntries.length) * 100) : 0;
    const pctReal = filledEntries.length > 0 ? Math.round((filledEntries.filter(e => e.clasificacion === 'avance_real').length / filledEntries.length) * 100) : 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Completa al menos 6 entradas con acción y clasificación'); return; }
        setLoading(true);
        try {
            const cleaned = filledEntries.map(e => ({
                accion: e.accion,
                sensacion_productividad: val(e.sensacion_productividad),
                impacto_real: val(e.impacto_real),
                clasificacion: e.clasificacion
            }));
            const { data } = await axios.post(`${API_URL}/api/user/modules/filtro-impacto/submit`,
                { entradas: cleaned }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setResultado({ pctPseudo, pctReal });
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <Filter className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-4">Filtro aplicado</h2>
                        <div className="flex justify-center gap-8 mb-4">
                            <div className="text-center"><p className="text-3xl font-bold text-[#5FAF9D]">{resultado.pctReal}%</p><p className="text-xs text-[#7A8A93]">Avance real</p></div>
                            <div className="text-center"><p className="text-3xl font-bold text-[#E8B27A]">{resultado.pctPseudo}%</p><p className="text-xs text-[#7A8A93]">Pseudoavance</p></div>
                        </div>
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div>
                            </div>
                        )}
                        <div className="mb-6"><ModuleFeedback moduleId="filtro_impacto" chapter={3} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar</Button></Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 3</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 3.3</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Filtro de impacto real</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                            <span className="text-sm text-[#7A8A93]">{filledEntries.length}/6 mín.</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <Filter className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">¿Estás avanzando o solo te sientes productivo?</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Compara la sensación de productividad con el impacto real</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        {entradas.map((entry, idx) => (
                            <div key={idx} className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8] space-y-3" data-testid={`impact-entry-${idx}`}>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-bold text-[#3E5F6E] bg-[#3E5F6E]/10 px-2 py-1 rounded">{idx + 1}</span>
                                    <input type="text" value={entry.accion} onChange={e => updateEntry(idx, 'accion', e.target.value)}
                                        placeholder="¿Qué acción realizaste?"
                                        className="flex-1 px-3 py-2 text-sm border border-[#DCE3E8] rounded-lg bg-white focus:ring-1 focus:ring-[#5FAF9D] focus:border-[#5FAF9D] outline-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-xs text-[#7A8A93]">Sensación productividad</Label>
                                            <span className="text-sm font-bold text-[#E8B27A]">{val(entry.sensacion_productividad)}</span>
                                        </div>
                                        <Slider value={[val(entry.sensacion_productividad)]} onValueChange={v => updateEntry(idx, 'sensacion_productividad', v[0])} max={10} min={0} step={1} />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-xs text-[#7A8A93]">Impacto real</Label>
                                            <span className="text-sm font-bold text-[#5FAF9D]">{val(entry.impacto_real)}</span>
                                        </div>
                                        <Slider value={[val(entry.impacto_real)]} onValueChange={v => updateEntry(idx, 'impacto_real', v[0])} max={10} min={0} step={1} />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {CLASIFICACIONES.map(cls => (
                                        <button key={cls.id} onClick={() => updateEntry(idx, 'clasificacion', cls.id)}
                                            className={`px-3 py-1.5 text-xs rounded-lg transition-all ${entry.clasificacion === cls.id ? `${cls.bg} text-white shadow-md` : 'bg-white text-[#7A8A93] border border-[#DCE3E8]'}`}>
                                            {cls.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md"
                                data-testid="submit-filtro">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar módulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
